import moment from 'moment';
import React, {useMemo, useState} from 'react';
import {ScrollView, StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import {useSelector} from 'react-redux';
import {RootState} from '../../redux/store';
import {theme} from '../../theme/theme';
import ReportChartCard from './ReportChartCard';
import {permittedReportCharts} from './registry';

import T from '../../Components/T';
const RANGES = [
  {key: 'today', label: 'Today'},
  {key: '7d', label: '7 Days'},
  {key: 'mtd', label: 'This Month'},
  {key: '30d', label: '30 Days'},
] as const;

type RangeKey = (typeof RANGES)[number]['key'];

export const rangeDates = (key: RangeKey) => {
  const from =
    key === 'today'
      ? moment()
      : key === '7d'
      ? moment().subtract(6, 'days')
      : key === 'mtd'
      ? moment().startOf('month')
      : moment().subtract(29, 'days');
  return {fdt: from.format('YYYY-MM-DD'), tdt: moment().format('YYYY-MM-DD')};
};

const ReportCharts = () => {
  const gdata = useSelector((state: RootState) => state.gdata);
  const [range, setRange] = useState<RangeKey>('mtd');
  const {fdt, tdt} = useMemo(() => rangeDates(range), [range]);
  const entries = useMemo(() => permittedReportCharts(gdata.menu), [gdata.menu]);

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <View style={styles.ranges}>
        {RANGES.map(r => (
          <TouchableOpacity
            key={r.key}
            style={[styles.chip, range === r.key && styles.chipActive]}
            onPress={() => setRange(r.key)}>
            <Text style={[styles.chipText, range === r.key && styles.chipTextActive]}>
              {r.label}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
      <Text style={styles.dates}>
        {moment(fdt).format('DD MMM YYYY')} – {moment(tdt).format('DD MMM YYYY')}
      </Text>
      {entries.length === 0 ? (
        <Text style={styles.empty}><T>No chartable reports are available to you.</T></Text>
      ) : (
        entries.map(e => (
          <ReportChartCard key={e.key} entry={e} cid={gdata.company} fdt={fdt} tdt={tdt} />
        ))
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: theme.colors.backgroundSecondary},
  content: {padding: 12, paddingBottom: 80},
  ranges: {flexDirection: 'row', gap: 8, flexWrap: 'wrap'},
  chip: {paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, borderWidth: 1, borderColor: theme.colors.primary},
  chipActive: {backgroundColor: theme.colors.primary},
  chipText: {color: theme.colors.primary, fontSize: 12, fontWeight: '600'},
  chipTextActive: {color: theme.colors.white},
  dates: {color: theme.colors.darkGray, fontSize: 12, marginVertical: 8},
  empty: {textAlign: 'center', color: theme.colors.darkGray, marginTop: 40},
});

export default ReportCharts;
